/**
 * Regeln rund um Zusatzfelder, die Web und API identisch anwenden müssen:
 * Gültigkeit je Belegart, Vererbung in Folgebelege und die Auswertung der
 * berechneten Felder einer Positionszeile.
 */
import { evaluateFormula } from './formula.js';
import { formatWeeklySchedule, parseWeeklySchedule } from './weekly-schedule.js';
/** Verlustfreie Abbildung der historischen Rollenpräfixe auf strukturierte Scopes. */
export function parseLegacyCustomFieldGroup(legacyGroup) {
    const source = legacyGroup?.trim() ?? '';
    if (source.length === 0) {
        return { name: 'Weitere Angaben', partnerRoles: [], isSystem: true };
    }
    const match = /^(customer|supplier|prospect):\s*(.*)$/iu.exec(source);
    if (match === null) {
        return { name: source, partnerRoles: [], isSystem: false };
    }
    const roleByPrefix = {
        customer: 'CUSTOMER',
        supplier: 'SUPPLIER',
        prospect: 'PROSPECT',
    };
    const prefix = match[1]?.toLowerCase();
    const name = match[2]?.trim() ?? '';
    return {
        name: name.length === 0 ? 'Weitere Angaben' : name,
        partnerRoles: [roleByPrefix[prefix]],
        isSystem: name.length === 0,
    };
}
/** Mehrrollen-Partner sehen ein Feld, sobald mindestens eine freigegebene Rolle passt. */
export function customFieldMatchesPartnerRoles(restrictions, partnerRoles) {
    return (restrictions.length === 0 ||
        restrictions.some((restriction) => partnerRoles.includes(restriction)));
}
/** Ein Feld ohne Belegarten-Einschränkung gilt für jede Belegart. */
export function isCustomFieldApplicableTo(definition, documentType) {
    if (definition.applicableDocumentTypes.length === 0) {
        return true;
    }
    return definition.applicableDocumentTypes.includes(documentType);
}
/** Anzeigename in der Belegsprache; ohne englische Fassung greift die deutsche. */
export function customFieldLabelFor(definition, language) {
    const locale = language.toLowerCase().split('-')[0];
    const translated = definition.translations.find((translation) => translation.locale.toLowerCase() === locale);
    if (translated !== undefined) {
        return translated.label;
    }
    if (language.startsWith('en') && definition.labelEn !== null && definition.labelEn.length > 0) {
        return definition.labelEn;
    }
    return definition.label;
}
const BOOLEAN_LABELS = {
    tr: ['Evet', 'Hayır'],
    en: ['Yes', 'No'],
    fr: ['Oui', 'Non'],
    es: ['Sí', 'No'],
    ru: ['Да', 'Нет'],
    de: ['Ja', 'Nein'],
};
function booleanLabel(value, locale) {
    const pair = BOOLEAN_LABELS[locale.slice(0, 2)] ?? BOOLEAN_LABELS['de'];
    return value ? (pair?.[0] ?? 'Ja') : (pair?.[1] ?? 'Nein');
}
function optionLabel(option, locale) {
    const language = locale.toLowerCase().split('-')[0];
    return (option.translations.find((translation) => translation.locale.toLowerCase() === language)
        ?.label ?? option.label);
}
/** Anzeigewert eines erfassten Zusatzfeldes; leere Werte liefern eine leere Zeichenkette. */
export function formatCustomFieldValue(definition, value, locale) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    switch (definition.fieldType) {
        case 'WEEKLY_SCHEDULE': {
            const schedule = parseWeeklySchedule(value);
            return schedule === null ? '' : formatWeeklySchedule(schedule, locale);
        }
        case 'CHECKBOX':
            return booleanLabel(value === true || value === 'true', locale);
        case 'DATE': {
            if (typeof value !== 'string' || value.length === 0) {
                return '';
            }
            const parsed = new Date(`${value}T00:00:00Z`);
            return Number.isNaN(parsed.getTime())
                ? value
                : new Intl.DateTimeFormat(locale, { timeZone: 'UTC' }).format(parsed);
        }
        case 'NUMBER':
        case 'DECIMAL': {
            const numeric = typeof value === 'number' ? value : Number(String(value));
            return Number.isNaN(numeric) ? String(value) : new Intl.NumberFormat(locale).format(numeric);
        }
        case 'SELECT': {
            const option = definition.options.find((entry) => entry.value === value);
            return option === undefined ? String(value) : optionLabel(option, locale);
        }
        case 'MULTI_SELECT': {
            if (!Array.isArray(value)) {
                return '';
            }
            return value
                .map((entry) => {
                const option = definition.options.find((candidate) => candidate.value === entry);
                return option === undefined ? entry : optionLabel(option, locale);
            })
                .join(', ');
        }
        default:
            return Array.isArray(value) ? value.join(', ') : String(value);
    }
}
export function activeCustomFieldsFor(definitions, documentType) {
    return definitions.filter((definition) => definition.isActive && isCustomFieldApplicableTo(definition, documentType));
}
function toFormulaValue(value) {
    if (value === null || value === undefined || Array.isArray(value)) {
        return null;
    }
    if (typeof value === 'boolean') {
        return value ? '1' : '0';
    }
    return typeof value === 'number' ? String(value) : value;
}
function appendScope(context, prefix, values) {
    for (const [key, value] of Object.entries(values)) {
        context[`${prefix}${key}`] = toFormulaValue(value);
    }
}
/** Baut den flachen Variablenraum einer Positionszeile für die Formel-Engine. */
export function buildFormulaContext(scope) {
    const context = { ...scope.line };
    appendScope(context, 'item.customFields.', scope.item);
    appendScope(context, 'product.customFields.', scope.product);
    appendScope(context, 'document.customFields.', scope.document);
    return context;
}
/**
 * Wertet alle Formelfelder einer Zeile aus. Ein fehlerhafter Ausdruck liefert
 * `null` statt eines Abbruchs — die Definition wird beim Speichern geprüft,
 * eine später ungültig gewordene Formel darf keinen Beleg blockieren.
 */
export function evaluateItemFormulas(definitions, scope) {
    const context = buildFormulaContext(scope);
    const result = {};
    for (const definition of definitions) {
        if (definition.fieldType !== 'FORMULA' || definition.formulaExpression === null) {
            continue;
        }
        let value = null;
        try {
            value = evaluateFormula(definition.formulaExpression, context, definition.formulaDecimals);
        }
        catch {
            value = null;
        }
        result[definition.key] = {
            value,
            unit: definition.formulaUnit,
            decimals: definition.formulaDecimals,
        };
    }
    return result;
}
/**
 * Wertet berechnete Kundenfelder für einen Beleg-Snapshot aus. Der Kontext enthält
 * ausschließlich numerisch interpretierbare Kunden-Zusatzfelder; unbekannte oder
 * zyklische Verweise liefern sicher `null` und führen nie Code aus.
 */
export function evaluateCustomerFormulas(definitions, customerValues) {
    const context = {};
    appendScope(context, 'customer.customFields.', customerValues);
    const result = {};
    for (const definition of definitions) {
        if (definition.fieldType !== 'FORMULA' || definition.formulaExpression === null)
            continue;
        let value = null;
        try {
            value = evaluateFormula(definition.formulaExpression, context, definition.formulaDecimals);
        }
        catch {
            value = null;
        }
        result[definition.key] = {
            value,
            unit: definition.formulaUnit,
            decimals: definition.formulaDecimals,
        };
    }
    return result;
}
/**
 * Wertet ausgewählte Belegkopf-Formelfelder aus. Der Kontext besteht nur aus
 * normalisierten Beleg-Zusatzfeldern; die sichere Parser-Engine führt keinen Code aus.
 */
export function evaluateDocumentFormulas(definitions, documentValues) {
    const context = {};
    appendScope(context, 'document.customFields.', documentValues);
    const result = {};
    // Formelfelder dürfen keine alten oder gegenseitig abhängigen Ergebnisse als
    // Eingabe verwenden; dadurch bleiben Vorschau und gespeicherter Snapshot deterministisch.
    for (const definition of definitions) {
        if (definition.fieldType === 'FORMULA') {
            delete context[`document.customFields.${definition.key}`];
        }
    }
    for (const definition of definitions) {
        if (definition.fieldType !== 'FORMULA' || definition.formulaExpression === null)
            continue;
        let value = null;
        try {
            value = evaluateFormula(definition.formulaExpression, context, definition.formulaDecimals);
        }
        catch {
            value = null;
        }
        result[definition.key] = {
            value,
            unit: definition.formulaUnit,
            decimals: definition.formulaDecimals,
        };
    }
    return result;
}
/**
 * Übernimmt Artikel-Stammdatenwerte in eine Belegposition. Bereits erfasste
 * Positionswerte bleiben erhalten; sie wurden bewusst überschrieben.
 */
export function mergeProductCustomFields(definitions, itemValues, productValues) {
    const merged = { ...itemValues };
    for (const definition of definitions) {
        if (!definition.isActive || !definition.copyToDocumentItemOnSelect) {
            continue;
        }
        const current = merged[definition.key];
        if (current !== undefined && current !== null) {
            continue;
        }
        const source = productValues[definition.key];
        if (source !== undefined && source !== null) {
            merged[definition.key] = source;
        }
    }
    return merged;
}
/**
 * Filtert Werte für einen Folgebeleg: Es überleben nur Felder, die vererbt
 * werden dürfen und für die Zielbelegart freigegeben sind.
 */
export function inheritCustomFieldValues(definitions, values, targetType) {
    const inherited = {};
    for (const definition of definitions) {
        if (!definition.inheritAcrossWorkflow || !isCustomFieldApplicableTo(definition, targetType)) {
            continue;
        }
        if (definition.fieldType === 'FORMULA') {
            // Nur die bewusste Auswahl wird übertragen. Der Zielbeleg berechnet seinen
            // eigenen Snapshot aus den dort geerbten Eingabefeldern neu.
            if (Object.hasOwn(values, definition.key))
                inherited[definition.key] = null;
            continue;
        }
        const value = values[definition.key];
        if (value !== undefined && value !== null) {
            inherited[definition.key] = value;
        }
    }
    return inherited;
}
//# sourceMappingURL=custom-fields.js.map